import React, { useState, useEffect, useRef, useCallback } from "react";
import { useApi } from "../utils/Api";

const MenuPanel = ({ cartItems, isInCart, setSelectedItem }) => {
  const [categories, setCategories] = useState([]); // always array
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [scrollingToCategory, setScrollingToCategory] = useState(false);

  const tabsRef = useRef(null);
  const tabRefs = useRef({});
  const categoryRefs = useRef({});

  const { apiFetch } = useApi();

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchCategories(), fetchMenuItems()]);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await apiFetch("categories");
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const data = await apiFetch("menu-items");
      setMenuItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching menu items:", err);
      setMenuItems([]);
    }
  };

  const getMenuItemsForCategory = (id) =>
    menuItems.filter((item) => Number(item.category_id) === Number(id));

  const getFilteredItemsForCategory = (categoryId) =>
    getMenuItemsForCategory(categoryId).filter((item) =>
      item.item_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const filteredCategories = (categories || [])
    .map((cat) => {
      const parentItems = getFilteredItemsForCategory(cat.id);

      const subcategoriesWithItems = (cat.subcategories || [])
        .map((sub) => {
          const filteredSubItems = getFilteredItemsForCategory(sub.id);
          return { ...sub, filteredItems: filteredSubItems };
        })
        .filter((sub) => sub.filteredItems.length > 0); 

      const totalItemsCount =
        parentItems.length +
        subcategoriesWithItems.reduce(
          (acc, sub) => acc + sub.filteredItems.length,
          0
        );

      if (searchTerm && totalItemsCount === 0) return null;

      return { ...cat, parentItems, subcategoriesWithItems, totalItemsCount };
    })
    .filter(Boolean);

  useEffect(() => {
    if (searchTerm === "" && filteredCategories.length > 0) {
      setActiveCategory(filteredCategories[0].id);
    }
  }, [searchTerm, filteredCategories]);

  const scrollToCategory = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    const el = categoryRefs.current[categoryId];
    if (!el) return;
    const headerOffset = 160;
    const elementPosition = el.getBoundingClientRect().top;
    const offsetPosition = window.scrollY + elementPosition - headerOffset;
    setScrollingToCategory(true);
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    setTimeout(() => setScrollingToCategory(false), 600);
  }, []);

  const updateActiveCategoryFromScroll = useCallback(() => {
    if (scrollingToCategory) return;
    const scrollPos = window.scrollY + 160;
    for (let category of filteredCategories) {
      const el = categoryRefs.current[category.id];
      if (!el) continue;
      const top = el.offsetTop;
      const bottom = top + el.offsetHeight;
      if (scrollPos >= top && scrollPos < bottom) {
        setActiveCategory(category.id);
        break;
      }
    }
  }, [filteredCategories, scrollingToCategory]);

  useEffect(() => {
    if (filteredCategories.length === 0) return;
    updateActiveCategoryFromScroll();
    window.addEventListener("scroll", updateActiveCategoryFromScroll);
    return () =>
      window.removeEventListener("scroll", updateActiveCategoryFromScroll);
  }, [filteredCategories, updateActiveCategoryFromScroll]);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const checkScroll = () => {
      setShowLeftArrow(el.scrollLeft > 0);
      setShowRightArrow(el.scrollWidth > el.clientWidth + el.scrollLeft + 1);
    };
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [categories, searchTerm]);

  const scrollLeft = () =>
    tabsRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  const scrollRight = () =>
    tabsRef.current?.scrollBy({ left: 150, behavior: "smooth" });

  useEffect(() => {
    if (!activeCategory) return;
    const tabEl = tabRefs.current[activeCategory];
    const container = tabsRef.current;
    if (tabEl && container) {
      const tabLeft = tabEl.offsetLeft;
      const tabWidth = tabEl.clientWidth;
      const containerWidth = container.clientWidth;
      const scrollLeftPos = container.scrollLeft;
      if (
        tabLeft < scrollLeftPos ||
        tabLeft + tabWidth > scrollLeftPos + containerWidth
      ) {
        container.scrollTo({
          left: tabLeft - containerWidth / 2 + tabWidth / 2,
          behavior: "smooth",
        });
      }
    }
  }, [activeCategory]);

  const renderMenuItem = (item) => {
    const inStock = Number(item.in_stock) === 1;
    return (
      <div
        key={item.id}
        className={`menu-item ${isInCart(item.id) ? "in-cart" : ""} ${
          !inStock ? "out-of-stock" : ""
        }`}
        onClick={() => inStock && setSelectedItem(item)}
      >
        <div className="menu-item-top">
          <div className="menu-item-top-left">
            <h3 className="menu-item-name">{item.item_name}</h3>
            {!inStock && (
              <span className="out-of-stock-ticker">Out of Stock</span>
            )}
          </div>
          <img
            src={`https://lasgidirestaurant.com/${item.image}`}
            alt={item.item_name}
            className="menu-item-img"
          />
        </div>
        <div className="menu-item-bottom">
          <div className="menu-item-bottom-left">
            {item.price > 0 ? (
              <p className="menu-item-price">£{item.price}</p>
            ) : (
              <p className="menu-item-price">&nbsp;</p>
            )}
          </div>
          <button className="add-btn" disabled={!inStock}>
            +
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="menu-panel">
      {/* Tabs + Search */}
      <div className="sticky-category-tabs">
        {!showSearch ? (
          <>
            <div className="category-tabs-wrapper">
              {showLeftArrow && (
                <i
                  className="fa fa-chevron-left scroll-arrow"
                  onClick={scrollLeft}
                />
              )}
              <div className="category-tabs" ref={tabsRef}>
                {(filteredCategories || []).map((category) => (
                  <button
                    key={category.id}
                    ref={(el) => (tabRefs.current[category.id] = el)}
                    className={`tab ${
                      activeCategory === category.id ? "active" : ""
                    }`}
                    onClick={() => scrollToCategory(category.id)}
                  >
                    {category.category_name}
                  </button>
                ))}
              </div>
              {showRightArrow && (
                <i
                  className="fa fa-chevron-right scroll-arrow"
                  onClick={scrollRight}
                />
              )}
            </div>
            <div className="tab-icons">
              <div className="separator"></div>
              <i className="fa fa-search" onClick={() => setShowSearch(true)} />
            </div>
          </>
        ) : (
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search menu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              className="close-search"
              onClick={() => {
                setSearchTerm("");
                setShowSearch(false);
              }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        )}
      </div>

      {/* Menu Items */}
      <div className="menu-container">
        {isLoading && <p className="no-items">Loading items...</p>}
        {(filteredCategories || []).length === 0 && !isLoading && (
          <p className="no-items">No items</p>
        )}

        {(filteredCategories || []).map((category) => (
          <div
            key={category.id}
            ref={(el) => (categoryRefs.current[category.id] = el)}
            id={`category-${category.id}`}
            className="menu-category"
          >
            <h2 className="category-heading">{category.category_name}</h2>
            <div className="menu-grid">
              {category.parentItems.length > 0
                ? category.parentItems.map(renderMenuItem)
                : category.totalItemsCount === 0 && (
                    <p className="no-items">No items</p>
                  )}

              {category.subcategoriesWithItems.map((sub) => (
                <div key={sub.id} className="subcategory">
                  <h3 className="subcategory-heading">{sub.category_name}</h3>
                  <div className="menu-grid">
                    {sub.filteredItems.length > 0 ? (
                      sub.filteredItems.map(renderMenuItem)
                    ) : (
                      <p className="no-items">No items</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MenuPanel;
